import { useCallback, useEffect, useRef, useState } from 'react';

import _ from 'lodash';

import { GroupItemsStatus } from '../../VendorRequestDetailDrawer.constants';

import {
  EMPTY_ITEM_FILTERS,
  fetchCorrelatedPage,
  fetchUncorrelatedItem,
  INITIAL_STATE,
} from './utils';

export const useGroupItems = ({ group, applicationId, itemFilters = EMPTY_ITEM_FILTERS }) => {
  const [state, setState] = useState(INITIAL_STATE);
  const requestTokenRef = useRef(0);

  const groupKey = _.get(group, 'groupKey', null);
  const correlationId = _.get(group, 'correlationId', null);

  useEffect(() => {
    const token = (requestTokenRef.current += 1);

    if (_.isNil(groupKey)) {
      return;
    }

    const lifecycle = { active: true };
    const isCurrent = () => lifecycle.active && token === requestTokenRef.current;

    // Awaiting a microtask first keeps the loading transition out of the synchronous effect body
    // (cascading-render lint) — the drawer stays closed-or-stale for one tick, never visibly.
    const load = async () => {
      await Promise.resolve();

      if (!isCurrent()) {
        return;
      }

      setState({ ...INITIAL_STATE, status: GroupItemsStatus.LOADING });

      try {
        const page = _.isNil(correlationId)
          ? await fetchUncorrelatedItem(groupKey)
          : await fetchCorrelatedPage({ applicationId, correlationId, skip: 0, itemFilters });

        if (isCurrent()) {
          setState({ ...page, status: GroupItemsStatus.LOADED });
        }
      } catch {
        if (isCurrent()) {
          setState({ ...INITIAL_STATE, status: GroupItemsStatus.ERROR });
        }
      }
    };

    load();

    return () => {
      lifecycle.active = false;
    };
  }, [groupKey, correlationId, applicationId, itemFilters]);

  const loadMore = useCallback(() => {
    if (_.isNil(correlationId)) {
      return;
    }

    const token = requestTokenRef.current;

    setState(prev => ({ ...prev, status: GroupItemsStatus.LOADING_MORE }));

    fetchCorrelatedPage({ applicationId, correlationId, skip: state.items.length, itemFilters })
      .then(page => {
        if (token === requestTokenRef.current) {
          setState(prev => ({
            items: [...prev.items, ...page.items],
            totalCount: page.totalCount,
            status: GroupItemsStatus.LOADED,
          }));
        }
      })
      .catch(() => {
        if (token === requestTokenRef.current) {
          setState(prev => ({ ...prev, status: GroupItemsStatus.ERROR }));
        }
      });
  }, [applicationId, correlationId, state.items.length, itemFilters]);

  return { ...state, hasMore: state.items.length < state.totalCount, loadMore };
};
